import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
  Sortable,
} from "./utils";
import type {
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
  CommonResponse,
  GenericResponse,
  Pageable,
} from "./utils";
import type { StandardObject } from "utils";

export interface ClientService {
  listUser: ReturnType<typeof listUser>;
  getUserDetail: ReturnType<typeof getUserDetail>;
  putUserDetail: ReturnType<typeof putUserDetail>;
  deleteUserDetail: ReturnType<typeof deleteUserDetail>;
  putUserPersonalInfo: ReturnType<typeof putUserPersonalInfo>;
  putUserIdentityInfo: ReturnType<typeof putUserIdentityInfo>;
  putUserDevice: ReturnType<typeof putUserDevice>;
  putUserPromotionNotification: ReturnType<typeof putUserPromotionNotification>;
}

export type AppUserStatus = "ACTIVE" | "DISABLED" | "LOCKED" | "TEMPORARY";
type AppUserLanguage =
  | "en-US"
  | "en-HK"
  | "zh-HK"
  | "zh-CN"
  | "ja-JP"
  | "sv-SE";

export interface AppUserSearchCriteria extends Sortable {
  id?: number;
  tenantId?: number;
  mobile?: string;
  contactEmail?: string;
  status?: AppUserStatus[];
  preferredLanguage?: AppUserLanguage[];
  lastLoginIp?: string;
  "lastLoginTime.from"?: string;
  "lastLoginTime.to"?: string;
  "createTime.from"?: string;
  "createTime.to"?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
  idNumber?: string;
}

export interface AppUserDTO {
  id: number;
  tenantId: number;
  mobileNumberHash: string;
  mobileNumberDisplay?: string;
  contactEmail?: string;
  status?: AppUserStatus;
  preferredLanguage?: AppUserLanguage;
  lastLoginIp?: string;
  lastLoginTime?: string;
  createTime?: string;
  lastUpdateTime?: string;
}

export interface AppUserDeviceDTO {
  id: number;
  deviceLabel: string;
  model: string;
  deviceOs: string;
  deviceOsVersion: string;
  deviceName: string;
  pushProvider: string;
  pushToken: string;
  additionalInfo: StandardObject;
}

export interface AppUserPersonalInfoDTO {
  id: number;
  title: string;
  enFirstName: string;
  enLastName: string;
  enFullName: string;
  zhFirstName: string;
  dateOfBirth: string;
  sexual: "M" | "F";
  nationality: string;
  additionalInfo: StandardObject;
}

type AppUserIdentityType = "HKID" | "PASSPORT";

export interface AppUserIdentityInfoDTO {
  id: number;
  type: AppUserIdentityType;
  idNumber: string;
  idNumberHash: string;
  issueDate: string;
  expiryDate: string;
  firstIssueDate: string;
  additionalInfo: StandardObject;
}

export interface AppUserPromotionOptionsDTO {
  optOutInAppApplicationNotices: boolean;
  optOutAnyPromotions: boolean;
  optOutSmsPromotion: boolean;
  optOutMailPromotion: boolean;
  optOutPhonePromotion: boolean;
  optOutInAppPromotion: boolean;
  optOutEmailPromotion: boolean;
}


export interface AppUserDetail extends AppUserDTO {
  mobileNumber: string;
  device?: AppUserDeviceDTO
  personalInfo?: AppUserPersonalInfoDTO;
  identityInfo?: AppUserIdentityInfoDTO;
  promotionOptions?: AppUserPromotionOptionsDTO;
}

export interface AppUserUpdateRequest {
  mobileNumber: string;
  contactEmail?: string;
  status?: AppUserStatus;
  preferredLanguage?: AppUserLanguage;
}

export interface AppUserIdentityInfoUpdateRequest {
  type: AppUserIdentityType;
  idNumber: string;
  issueDate: string;
  expiryDate: string;
  firstIssueDate: string;
  additionalInfo: StandardObject;
}

export interface AppUserDeviceUpdateRequest {
  deviceLabel: string;
  model: string;
  deviceOs: string;
  deviceOsVersion: string;
  deviceName: string;
  pushProvider: string;
  pushToken: string;
  additionalInfo: StandardObject;
}

export const listUser: WrappedCashifyPageableQuery<
  AppUserSearchCriteria,
  AppUserDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/fuboncredit/app/user`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getUserDetail: WrappedCashifyFunction<
  { id: number },
  GenericResponse<AppUserDetail>
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putUserDetail: WrappedCashifyFunction<
  { id: number; detail: AppUserUpdateRequest },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteUserDetail: WrappedCashifyFunction<
  { id: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putUserPersonalInfo: WrappedCashifyFunction<
  { id: number; detail: AppUserPersonalInfoDTO },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}/personal-info`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putUserIdentityInfo: WrappedCashifyFunction<
  { id: number; detail: AppUserIdentityInfoUpdateRequest },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}/identity-info`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putUserDevice: WrappedCashifyFunction<
  { id: number; detail: AppUserDeviceUpdateRequest }, 
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/${id}/device`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putUserPromotionNotification: WrappedCashifyFunction<
  { id: number; detail: AppUserPromotionOptionsDTO },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/app/user/promotion-notification`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
          "x-user-id": id,
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
